import React, { useState, useRef } from "react";
import MedicamentFieldsRenderer from "./renderer/MedicamentFieldsRenderer";
import MedicamentAutocomplete from "./MedicamentAutocomplete";

const inputStyle = {
    width: "100%",
    padding: "5px 8px",
    borderRadius: "6px",
    border: "1px solid #e2e8f0",
    fontSize: "13px",
    background: "white",
    boxSizing: "border-box"
};

const inputStyleGreen = {
    ...inputStyle,
    border: "1px solid #bbf7d0",
    background: "#f0fdf4"
};

const inputStyleOrange = {
    ...inputStyle,
    border: "1px solid #fb923c",
    background: "#ffedd5"
};

function selectedCip13(suggestion) {
    return String(
        suggestion?.cip13
        ?? suggestion?.cip13Code
        ?? suggestion?.cip13Codes
        ?? ""
    ).trim();
}

function Cip13CopyBadge({ code }) {
    const [copied, setCopied] = useState(false);
    const [hovered, setHovered] = useState(false);
    if (!code) return null;

    const showCopyIcon = hovered || copied;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch {
            setCopied(false);
        }
    };

    return (
        <button
            type="button"
            onClick={copy}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onFocus={() => setHovered(true)}
            onBlur={() => setHovered(false)}
            aria-label={copied ? "Code CIP13 copié" : "Cliquer pour copier"}
            title={copied ? "Code CIP13 copié" : "Cliquer pour copier"}
            style={{
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                border: "1px solid #99f6e4",
                background: copied ? "#ccfbf1" : "#f0fdfa",
                color: "#0f766e",
                borderRadius: "6px",
                padding: "3px 6px",
                fontSize: "11px",
                fontWeight: 700,
                lineHeight: 1.2,
                whiteSpace: "nowrap"
            }}
        >
            <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                width="13"
                height="13"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0, opacity: showCopyIcon ? 1 : 0 }}
            >
                <rect x="8" y="8" width="12" height="12" rx="2" />
                <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
            </svg>
            CIP13: {code}
        </button>
    );
}

function BdpmDisponibiliteBadge({ statut }) {
    const [visible, setVisible] = useState(false);
    if (!statut) return null;

    const value = String(statut || "");
    const unavailable = /rupture de stock/i.test(value);
    if (!unavailable) return null;

    return (
        <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
            <button
                type="button"
                aria-label="Indisponible - rupture de stock"
                onMouseEnter={() => setVisible(true)}
                onMouseLeave={() => setVisible(false)}
                onFocus={() => setVisible(true)}
                onBlur={() => setVisible(false)}
                style={{
                    width: "22px",
                    height: "22px",
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid #fecaca",
                    background: "#fef2f2",
                    color: "#b91c1c",
                    borderRadius: "999px",
                    fontSize: "13px",
                    fontWeight: 700,
                    lineHeight: 1,
                    cursor: "help",
                    padding: 0
                }}
            >
                !
            </button>
            {visible && (
                <span
                    role="tooltip"
                    style={{
                        position: "absolute",
                        left: "50%",
                        bottom: "calc(100% + 7px)",
                        transform: "translateX(-50%)",
                        zIndex: 30,
                        padding: "6px 8px",
                        borderRadius: "6px",
                        background: "#111827",
                        color: "white",
                        fontSize: "11px",
                        fontWeight: 700,
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        boxShadow: "0 6px 16px rgba(15, 23, 42, 0.18)"
                    }}
                >
                    Rupture de stock
                </span>
            )}
        </span>
    );
}

function medicamentRowVisualState(isTraite, isValidated, isEnProduction) {
    if (isTraite) {
        return { background: "#f0fdf4", border: "#bbf7d0", input: inputStyleGreen };
    }
    if (isValidated || isEnProduction) {
        return { background: "#ffedd5", border: "#fb923c", input: inputStyleOrange };
    }
    return { background: "white", advancedBackground: "#f8fafc", border: "#f1f5f9", input: inputStyle };
}

function ProductStateBadges({ isTraite, isValidated, isEnProduction, managementValidated, pharmaceuticalValidated }) {
    return (
        <>
            {isTraite && (
                <span style={treatedBadgeStyle}>✓ Traité</span>
            )}
            {!isTraite && (isValidated || isEnProduction) && (
                <span style={productionBadgeStyle}>⚙ En production</span>
            )}
            {pharmaceuticalValidated && (
                <span style={pharmaceuticalBadgeStyle}>Validé pharma</span>
            )}
            {managementValidated && !pharmaceuticalValidated && (
                <span style={pharmaceuticalBadgeStyle}>Saisie validée</span>
            )}
        </>
    );
}

function AdvancedFieldsRow({ visible, background, colCount, borderColor, prod, strategy, onUpdate, index, commande }) {
    if (!visible) return null;
    return (
        <tr style={{ background }}>
            <td colSpan={colCount} style={{ padding: "12px 16px", borderBottom: `1px solid ${borderColor}` }}>
                <MedicamentFieldsRenderer
                    produit={prod}
                    strategy={strategy}
                    onChange={(fieldOrPatch, value) => onUpdate(index, fieldOrPatch, value)}
                    commande={commande}
                />
            </td>
        </tr>
    );
}

function updateSiBesoin(onUpdate, index, checked) {
    if (checked) {
        onUpdate(index, { siBesoin: true, destination: "ETIQUETAGE" });
        return;
    }
    onUpdate(index, "siBesoin", false);
}

export default function MedicamentRow({
    prod,
    index,
    strategy,
    colCount,
    onUpdate,
    onRemove,
    autoFocus,
    onFocused,
    commande,
    destinationError,
    productSelectionMode,
    selectedForValidation,
    onToggleValidationSelection,
}) {
    const [expanded, setExpanded] = useState(false);
    const posologieRef = useRef(null);

    const isTraite = prod.statut === "TRAITE";
    const isEnProduction = !isTraite && !!prod.planificationValidated;
    const isValidated = !!prod.validated;

    const showAdvanced =
        (prod.destination === "ROBOT" && strategy?.showRobotFields) ||
        (prod.destination === "ETIQUETAGE" && strategy?.showEtiquetageFields);

    const showDestination = strategy?.showDestination !== false;
    const showDosage = !!strategy?.showDosage;
    const showSiBesoin = !!strategy?.showSiBesoin;
    const showClassificationMedicament = !!strategy?.showClassificationMedicament;
    const showValidationCheckbox = !!strategy?.showValidationCheckbox;
    const workflowValidationField = strategy?.workflowValidationField || "pharmaceuticalValidated";
    const workflowValidated = !!prod[workflowValidationField];
    const destinationValue = prod.destination === "NONE" ? "" : (prod.destination || "");
    const cip13 = prod.cip13 || "";
    const disponibiliteStatut = prod.bdpmDisponibiliteStatuts || "";

    const handleSiBesoinChange = (checked) => updateSiBesoin(onUpdate, index, checked);

    const visualState = medicamentRowVisualState(isTraite, isValidated, isEnProduction);
    const rowBg = visualState.background;
    const rowBorderColor = visualState.border;
    const currentInputStyle = visualState.input;

    const tdStyle = {
        padding: "8px 10px",
        verticalAlign: "middle",
        borderBottom: `1px solid ${rowBorderColor}`
    };

    return (
        <>
            <tr style={{ background: rowBg }}>
                {productSelectionMode && (
                    <td style={{ ...tdStyle, width: "54px", textAlign: "center" }}>
                        <input
                            type="checkbox"
                            aria-label={`Sélectionner ${prod.nom || "ce produit"}`}
                            checked={!!selectedForValidation}
                            disabled={prod.id == null || workflowValidated}
                            onChange={() => onToggleValidationSelection?.(prod.id)}
                            style={{ width: "17px", height: "17px", cursor: prod.id != null && !workflowValidated ? "pointer" : "not-allowed", accentColor: "#17B978" }}
                        />
                    </td>
                )}
                {/* Médicament */}
                <td style={{ ...tdStyle, minWidth: "240px", paddingTop: "12px", paddingBottom: "12px" }}>
                    <div style={{ display: "grid", gap: "7px", minWidth: 0 }}>
                        <MedicamentAutocomplete
                            value={prod.nom || ""}
                            onChange={val => onUpdate(index, {
                                nom: val,
                                codeCis: "",
                                cip13: "",
                                bdpmDisponibiliteStatutCodes: "",
                                bdpmDisponibiliteStatuts: ""
                            })}
                            onSelect={suggestion => onUpdate(index, {
                                nom: suggestion.denomination,
                                codeCis: suggestion.codeCis || "",
                                cip13: selectedCip13(suggestion),
                                bdpmDisponibiliteStatutCodes: suggestion.disponibiliteStatutCodes || "",
                                bdpmDisponibiliteStatuts: suggestion.disponibiliteStatuts || ""
                            })}
                            inputStyle={currentInputStyle}
                            autoFocus={autoFocus}
                            onFocused={onFocused}
                            onEnterConfirm={() => posologieRef.current?.focus()}
                        />
                        <div style={{ minHeight: "24px", display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                            <Cip13CopyBadge code={cip13} />
                            <BdpmDisponibiliteBadge statut={disponibiliteStatut} />
                            <ProductStateBadges
                                isTraite={isTraite}
                                isValidated={isValidated}
                                isEnProduction={isEnProduction}
                                managementValidated={prod.managementValidated}
                                pharmaceuticalValidated={prod.pharmaceuticalValidated}
                            />
                        </div>
                    </div>
                </td>

                {/* Dosage */}
                {showDosage && (
                    <td style={tdStyle}>
                        <input
                            style={currentInputStyle}
                            placeholder="ex: 500 mg"
                            value={prod.dosage || ""}
                            onChange={e => onUpdate(index, "dosage", e.target.value)}
                        />
                    </td>
                )}

                {/* Posologie */}
                <td style={tdStyle}>
                    <input
                        ref={posologieRef}
                        style={currentInputStyle}
                        placeholder="ex: 1 cp matin, 1 cp soir"
                        value={prod.posologie || ""}
                        onChange={e => onUpdate(index, "posologie", e.target.value)}
                    />
                </td>

                {/* Si besoin */}
                {showSiBesoin && (
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                        <input
                            type="checkbox"
                            checked={!!prod.siBesoin}
                            onChange={e => handleSiBesoinChange(e.target.checked)}
                            style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "#f97316" }}
                        />
                    </td>
                )}

                {/* Classification médicament */}
                {showClassificationMedicament && (
                    <td style={tdStyle}>
                        <select
                            style={{ ...currentInputStyle, cursor: "pointer", minWidth: "80px" }}
                            value={prod.classificationMedicament || ""}
                            onChange={e => onUpdate(index, "classificationMedicament", e.target.value || null)}
                        >
                            <option value="">—</option>
                            <option value="AJOUT_TTT">Ajout TTT</option>
                            <option value="MODIF_POSOLOGIE">Modif posologie</option>
                            <option value="DOSAGE">Dosage</option>
                            <option value="ARRET_TTT">Arrêt TTT</option>
                            <option value="CNO">CNO</option>
                            <option value="ORTHO_CONTENTIONS">Ortho/Contentions</option>
                            <option value="STUP">STUP</option>
                            <option value="MEDICAMENT_EXCEPTION">Médicament d'exception</option>
                        </select>
                    </td>
                )}

                {/* Qté */}
                <td style={{ ...tdStyle, width: "70px" }}>
                    <input
                        style={{ ...currentInputStyle, textAlign: "center" }}
                        type="number"
                        min="1"
                        value={prod.quantite ?? 1}
                        onChange={e => onUpdate(index, "quantite", Number.parseInt(e.target.value) || 1)}
                    />
                </td>

                {/* Destination */}
                {showDestination && (
                    <td style={tdStyle}>
                        <select
                            style={{
                                ...currentInputStyle,
                                cursor: prod.siBesoin ? "not-allowed" : "pointer",
                                opacity: prod.siBesoin ? 0.6 : 1,
                                ...(destinationError ? { border: "1.5px solid #ef4444", background: "#fff5f5" } : {}),
                            }}
                            value={destinationValue}
                            onChange={e => onUpdate(index, "destination", e.target.value)}
                            disabled={!!prod.siBesoin}
                        >
                            <option value="" disabled>Choisir…</option>
                            <option value="ROBOT">Robot</option>
                            <option value="ETIQUETAGE">Étiquetage</option>
                        </select>
                    </td>
                )}

                {/* Actions */}
                <td style={{ ...tdStyle, width: "70px" }}>
                    <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                        {showAdvanced && (
                            <button
                                type="button"
                                onClick={() => setExpanded(v => !v)}
                                title="Paramètres avancés"
                                style={{
                                    padding: "4px 7px",
                                    borderRadius: "6px",
                                    border: "1px solid #e2e8f0",
                                    background: expanded ? "#eff6ff" : "white",
                                    color: expanded ? "#2563eb" : "#64748b",
                                    cursor: "pointer",
                                    fontSize: "13px"
                                }}
                            >⚙</button>
                        )}
                        <button
                            type="button"
                            onClick={() => onRemove(index)}
                            title="Supprimer"
                            style={{
                                padding: "4px 7px",
                                borderRadius: "6px",
                                border: "1px solid #fecaca",
                                background: "#fff5f5",
                                color: "#ef4444",
                                cursor: "pointer",
                                fontSize: "13px"
                            }}
                        >🗑</button>
                    </div>
                </td>

                {/* Validation */}
                {showValidationCheckbox && (
                    <td style={{ ...tdStyle, width: "50px", textAlign: "center" }}>
                        <input
                            type="checkbox"
                            tabIndex={-1}
                            checked={isValidated}
                            onChange={e => onUpdate(index, "validated", e.target.checked)}
                            style={{ width: "17px", height: "17px", cursor: "pointer", accentColor: "#22c55e" }}
                        />
                    </td>
                )}
            </tr>

            {(prod.scannedLots || []).length > 0 && (
                <tr style={{ background: "#f0fdf4" }}>
                    <td colSpan={colCount} style={{ padding: "7px 12px", borderBottom: `1px solid ${rowBorderColor}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "7px", flexWrap: "wrap", fontSize: "12px", color: "#0C3B3C" }}>
                            <strong>Boîtes scannées</strong>
                            {prod.scannedLots.map(lot => (
                                <span
                                    key={`${lot.lotNumber}-${lot.expiryDate}`}
                                    style={{ padding: "3px 7px", borderRadius: "999px", background: "white", border: "1px solid #a7f3d0" }}
                                >
                                    Lot {lot.lotNumber} · DLU {lot.expiryDate
                                        ? new Intl.DateTimeFormat("fr-FR").format(new Date(`${lot.expiryDate}T00:00:00`))
                                        : "inconnue"} · {lot.quantity} boîte{lot.quantity > 1 ? "s" : ""}
                                </span>
                            ))}
                        </div>
                    </td>
                </tr>
            )}

            {/* Paramètres avancés (Robot / Étiquetage) */}
            <AdvancedFieldsRow
                visible={expanded && showAdvanced}
                background={visualState.advancedBackground || visualState.background}
                colCount={colCount}
                borderColor={rowBorderColor}
                prod={prod}
                strategy={strategy}
                onUpdate={onUpdate}
                index={index}
                commande={commande}
            />
        </>
    );
}

const treatedBadgeStyle = {
    flexShrink: 0,
    background: "#22c55e",
    color: "white",
    padding: "2px 7px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: 600,
    whiteSpace: "nowrap"
};

const productionBadgeStyle = {
    ...treatedBadgeStyle,
    background: "#f97316"
};

const pharmaceuticalBadgeStyle = {
    ...treatedBadgeStyle,
    background: "#dcfce7",
    color: "#166534",
    border: "1px solid #86efac",
    fontWeight: 700
};
